import { useAutocomplete } from 'admin';
import type { SimpleTradeCompanyListItem } from 'api';
import type { AutocompleteProps } from 'ui';

import { AutocompleteField } from '../AutocompleteField/AutocompleteField';

type TradeCompanyAutocompleteProps = {
  name: string;
  label?: string;
  required?: boolean;
  onlyBuyers?: boolean;
  onlySellers?: boolean;
  excludeKycBanned?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  defaultValue?: SimpleTradeCompanyListItem['id'];
  onSelectOption?: (value: SimpleTradeCompanyListItem) => void;
} & Omit<AutocompleteProps<SimpleTradeCompanyListItem>, 'options' | 'defaultValue'>;

export const TradeCompanyAutocomplete = ({
  name,
  label = 'Select company',
  onlyBuyers = false,
  onlySellers = false,
  excludeKycBanned = false,
  size,
  required,
  onSelectOption,
  disabled = false,
  defaultValue,
  ...rest
}: TradeCompanyAutocompleteProps) => {
  const { autocompleteProps } = useAutocomplete<SimpleTradeCompanyListItem>({
    dataProviderName: 'trade',
    defaultValue,
    defaultValueQueryOptions: {
      // We have to do this because the API used for this query returns extra information not returned by the normal API used for
      // this autocomplete. That causes issues by refine not recognizing if two options are the same and duplicating them
      select: (response) => ({
        data: response.data.map((site) => ({
          currency: site.currency,
          id: site.id,
          name: site.name,
          preferred_port_of_discharge_id: site.preferred_port_of_discharge_id,
          preferred_port_of_loading_id: site.preferred_port_of_loading_id,
        })),
      }),
    },
    filters: [
      {
        field: 'only_sellers',
        operator: 'eq',
        value: onlySellers,
      },
      {
        field: 'only_buyers',
        operator: 'eq',
        value: onlyBuyers,
      },
      {
        field: 'exclude_kyc_banned',
        operator: 'eq',
        value: excludeKycBanned,
      },
    ],
    onSearch: (value) => [
      {
        field: 'search',
        operator: 'contains',
        value,
      },
    ],
    resource: '/v1/trade-companies/',
  });

  return (
    <AutocompleteField<SimpleTradeCompanyListItem>
      {...autocompleteProps}
      getOptionLabel={(item) => item.name ?? ''}
      label={label}
      name={name}
      isOptionEqualToValue={(option, value) => {
        return value === undefined || option?.id?.toString() === (value?.id ?? value)?.toString();
      }}
      placeholder="Type to search"
      valueField="id"
      size={size}
      required={required}
      data-testid="trade-company-autocomplete"
      onSelectOption={onSelectOption}
      disabled={disabled}
      {...rest}
    />
  );
};
